import assetUtils from 'app-lib/assetUtils'
import numberUtils from 'app-lib/numberUtils'
import { IChainConfig } from 'bzx-common/src/config/chains/chainCommon'
import { ILendItem } from 'bzx-common/src/stores/LendStore/LendStore'
import { observer } from 'mobx-react'
import React from 'react'
import ExternalLink from 'shared-components/ExternalLink'
import TokenTip from 'shared-components/TokenTip'
import { Button, Icon, InfoTip } from 'ui-framework'
import LendVM from '../LendVM'

export interface ILendTokenSelectorItemProps {
  vm: LendVM
  lendItemDetails: ILendItem
  chain: IChainConfig
}

/**
 * Returns a twitter intent to ask people to borrow on Ooki at low rates.
 */
function getTweetLink(props: ILendTokenSelectorItemProps, chain: IChainConfig) {
  return `https://twitter.com/intent/tweet?original_referer=${encodeURIComponent(
    'https://ooki.com'
  )}&ref_src=twsrc%5Etfw%7Ctwcamp%5Ebuttonembed%7Ctwterm%5Emention%7Ctwgr%5EOokiTrade&screen_name=OokiTrade&text=${encodeURIComponent(
    `\n\nBorrowing $${props.lendItemDetails.asset} on ${chain.twitterHandle} is cheap at the moment! It's almost free money! 😎💰\n\nBorrow now on https://ooki.com/borrow.`
  )}`
}

function LendListItem(props: ILendTokenSelectorItemProps) {
  const { vm, lendItemDetails } = props
  const { asset, interestRate } = lendItemDetails
  const { chainId } = props.chain.chainDetails
  const assetInfo = assetUtils.getInfo(chainId, asset)
  const hasPendingTx = vm.lendStore.pendingAssetTx.some((txAsset) => txAsset === asset)
  const ratePrefix = interestRate.gt(0) && interestRate.lt(0.001) ? '~' : ''
  const isComingSoon = props.chain.comingSoon.tokens[asset]

  const rateTip =
    interestRate.lt(5) && interestRate.gt(0) ? (
      <InfoTip id={`lend-apr-tooltip${asset}`} top={-2} delayShow={150}>
        <div className="margin-bottom-1">
          <div className="txt-explanation padded-1 txt-left">
            <h4 className="h4">Ooki lending rates</h4>
            <p>Earn passive income with very high rates, 20%, 50% and much more!</p>
            <p>
              <i>When the pool reaches 80% of utilization</i>, meaning when <b>many</b> people trade
              and borrow on Ooki, you benefit from especially high rates.
            </p>
            <p>
              Spread the word, get people to use Ooki and start earning from great lending rates.
            </p>
          </div>

          <ExternalLink
            className="btn btn--blue btn--narrow block txt-center"
            href={getTweetLink(props, props.chain)}>
            Tell people to borrow <Icon name="twitter" size={24} title="Share on Twitter" top={2} />
          </ExternalLink>
        </div>
      </InfoTip>
    ) : null

  return (
    <div className="panel--simple padded-v-1 margin-bottom-1 md-padded-h-2">
      <div className="lend-list__grid items-center">
        <div className="flex-row-center">
          <Icon name={assetInfo.spriteLogo} size={38} className="lend-list__asset-logo" />
          {lendItemDetails.iTokenAddress ? (
            <TokenTip
              iTokenAddress={lendItemDetails.iTokenAddress}
              iTokenExplorerUrl={lendItemDetails.iTokenExplorerUrl}>
              <div className="txt-title txt-default margin-left-05">{assetInfo.displayName}</div>
            </TokenTip>
          ) : (
            <div className="txt-title txt-default margin-left-05">{assetInfo.displayName}</div>
          )}
        </div>
        <div className="txt-title txt-default lend-list__apr">
          <div className="txt-subtitle lend-list__apr-label">Lending Rate</div>
          <span
            title={`${interestRate && interestRate.toFixed()}%`}
            className={!rateTip ? 'lend-list__apr-offset' : ''}>
            {ratePrefix}
            {numberUtils.formatPercent(interestRate)}%
          </span>{' '}
          {rateTip}
        </div>

        {isComingSoon ? (
          <div>
            <span className="badge--green">Coming soon!</span>
          </div>
        ) : (
          <div>
            <div>
              <b title={lendItemDetails.availableLiquidity}>
                {numberUtils.abbreviate(lendItemDetails.availableLiquidity, 4)}
              </b>{' '}
              <span className="txt-subtitle">available</span>
            </div>
            <div>
              <b title={lendItemDetails.totalBorrow}>
                {numberUtils.abbreviate(lendItemDetails.totalBorrow, 4)}
              </b>{' '}
              <span className="txt-subtitle">borrowed</span>
            </div>
          </div>
        )}
        <div className="lend-list__last-col">
          <Button
            variant="blue"
            className="lend__btn"
            onClick={vm.showLendForm}
            onClickEmit="value"
            value={asset}
            disabled={isComingSoon}
            isLoading={hasPendingTx}>
            Lend
          </Button>
        </div>
      </div>
    </div>
  )
}

export default observer(LendListItem)
